import React, { useCallback, useEffect, useRef, useState } from "react";

import LibrarySubtabIntro from "./LibrarySubtabIntro";
import TipCard from "./TipCard";
import {
  LIBRARY_TYPE,
  trackSponsorTipsEvent,
  type SponsorTip,
} from "../../Utils/sponsorTipsConstants";
import { browseTipsGridClass } from "../../Utils/sponsorTipsGrouping";

type GroupedBrowseLayoutProps = {
  groups: [string, SponsorTip[]][];
  anchorPrefix: string;
  scrollRootRef?: React.RefObject<HTMLElement | null>;
  /** When set, skip hash-based scroll and defer scroll-spy until parent restores this offset */
  initialScrollTop?: number | null;
  navAriaLabel?: string;
  getLabel: (key: string) => string;
  getDescription?: (key: string) => string;
  telemetryEvent?: string;
  emptyMessage?: string;
  introTitle?: string;
  intro?: React.ReactNode;
  suppressCategoryBadge?: boolean;
  renderTipsGrid?: (groupKey: string, tips: SponsorTip[]) => React.ReactNode;
};

const getScrollParent = (node: HTMLElement): HTMLElement | Window => {
  let parent = node.parentElement;
  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    if (overflowY === "auto" || overflowY === "scroll") return parent;
    parent = parent.parentElement;
  }
  return window;
};

/** Section offset from the top of the scroll container's content box. */
const getSectionScrollTop = (
  root: HTMLElement,
  section: HTMLElement
): number => {
  const rootRect = root.getBoundingClientRect();
  const sectionRect = section.getBoundingClientRect();
  return sectionRect.top - rootRect.top + root.scrollTop;
};

const GroupedBrowseLayout: React.FC<GroupedBrowseLayoutProps> = ({
  groups,
  anchorPrefix,
  scrollRootRef,
  initialScrollTop = null,
  navAriaLabel = "Sections",
  getLabel,
  getDescription,
  telemetryEvent = "browse_nav_clicked",
  emptyMessage = "No items in this view.",
  introTitle,
  intro,
  suppressCategoryBadge = true,
  renderTipsGrid,
}) => {
  const [activeKey, setActiveKey] = useState(groups[0]?.[0] ?? null);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const scrollingToRef = useRef(false);
  const scrollEndTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollRafRef = useRef<number | null>(null);
  const pendingKeyRef = useRef<string | null>(null);
  const activeKeyRef = useRef<string | null>(groups[0]?.[0] ?? null);
  const navScrollRef = useRef<HTMLDivElement | null>(null);
  const stickyNavRef = useRef<HTMLDivElement | null>(null);
  const tabBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const groupsKey = groups.map(([key]) => key).join("\0");

  const anchorId = (key: string) => `${anchorPrefix}-${key}`;

  const getHashKey = useCallback(() => {
    const hash = window.location.hash.replace(/^#/, "");
    const prefix = `${anchorPrefix}-`;
    if (!hash.startsWith(prefix)) return null;
    const key = hash.slice(prefix.length);
    return groups.some(([k]) => k === key) ? key : null;
  }, [anchorPrefix, groups]);

  const hasPendingScrollRestore = initialScrollTop != null;

  /**
   * Distance from the scroll container's visible top to the content read line
   * (bottom of sticky nav + its margin). Shared by scroll-spy and click-scroll.
   */
  const getScrollAnchorOffset = useCallback((root: HTMLElement) => {
    const sticky = stickyNavRef.current;
    if (!sticky) return 96;

    const rootTop = root.getBoundingClientRect().top;
    const stickyRect = sticky.getBoundingClientRect();
    const marginBottom =
      Number.parseFloat(window.getComputedStyle(sticky).marginBottom) || 0;
    return stickyRect.bottom - rootTop + marginBottom;
  }, []);

  const syncHashToKey = useCallback(
    (key: string) => {
      const anchor = anchorId(key);
      if (window.location.hash.replace(/^#/, "") === anchor) return;
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${window.location.search}#${anchor}`
      );
    },
    [anchorPrefix]
  );

  const resolveActiveKeyFromScroll = useCallback(() => {
    const root = scrollRootRef?.current;
    if (!root || !groups.length) return null;

    const scrollAnchor = root.scrollTop + getScrollAnchorOffset(root);
    let currentKey: string = groups[0][0];

    for (const [key] of groups) {
      const el = sectionRefs.current[key];
      if (!el) continue;
      const sectionTop = getSectionScrollTop(root, el);
      if (sectionTop <= scrollAnchor + 2) {
        currentKey = key;
      } else {
        break;
      }
    }

    return currentKey;
  }, [groups, scrollRootRef, getScrollAnchorOffset]);

  const applyActiveKey = useCallback(
    (key: string, { syncHash = false } = {}) => {
      if (!key || activeKeyRef.current === key) return;
      activeKeyRef.current = key;
      setActiveKey(key);
      if (syncHash) syncHashToKey(key);
    },
    [syncHashToKey]
  );

  const finishScroll = useCallback(() => {
    scrollingToRef.current = false;
    if (pendingKeyRef.current) {
      applyActiveKey(pendingKeyRef.current);
      pendingKeyRef.current = null;
      return;
    }
    const resolved = resolveActiveKeyFromScroll();
    if (resolved) applyActiveKey(resolved, { syncHash: true });
  }, [resolveActiveKeyFromScroll, applyActiveKey]);

  const scrollToKey = useCallback(
    (
      groupKey: string,
      {
        updateHash = true,
        behavior = "smooth",
      }: { updateHash?: boolean; behavior?: ScrollBehavior } = {}
    ) => {
      const anchor = anchorId(groupKey);
      const el =
        sectionRefs.current[groupKey] || document.getElementById(anchor);
      if (!el) return;

      scrollingToRef.current = true;
      pendingKeyRef.current = groupKey;
      applyActiveKey(groupKey);
      if (updateHash) {
        window.history.replaceState(
          null,
          "",
          `${window.location.pathname}${window.location.search}#${anchor}`
        );
      }
      trackSponsorTipsEvent(telemetryEvent, { [anchorPrefix]: groupKey });

      const root = scrollRootRef?.current;
      if (root) {
        const sectionTop = getSectionScrollTop(root, el);
        root.scrollTo({
          top: Math.max(0, sectionTop - getScrollAnchorOffset(root)),
          behavior,
        });
      } else {
        el.scrollIntoView({ behavior, block: "start" });
      }

      if (behavior === "auto") {
        scrollingToRef.current = false;
        pendingKeyRef.current = null;
        return;
      }

      const scrollParent = root ?? getScrollParent(el);

      if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);

      const onScrollEnd = () => {
        if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);
        finishScroll();
      };

      const onScroll = () => {
        if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);
        scrollEndTimer.current = setTimeout(finishScroll, 150);
      };

      const supportsScrollEnd = "onscrollend" in window;
      if (supportsScrollEnd) {
        scrollParent.addEventListener("scrollend", onScrollEnd, { once: true });
        scrollEndTimer.current = setTimeout(() => {
          scrollParent.removeEventListener("scrollend", onScrollEnd);
          finishScroll();
        }, 1500);
      } else {
        scrollParent.addEventListener("scroll", onScroll, { passive: true });
        scrollEndTimer.current = setTimeout(() => {
          scrollParent.removeEventListener("scroll", onScroll);
          finishScroll();
        }, 1500);
      }
    },
    [
      anchorPrefix,
      telemetryEvent,
      finishScroll,
      scrollRootRef,
      getScrollAnchorOffset,
      applyActiveKey,
    ]
  );

  useEffect(() => {
    const hashKey = getHashKey();
    const hasValidHash = hashKey !== null;

    const syncFromScroll = () => {
      const resolved = resolveActiveKeyFromScroll();
      const next = resolved ?? groups[0]?.[0] ?? null;
      activeKeyRef.current = next;
      setActiveKey(next);
    };

    if (hasValidHash && hashKey) {
      activeKeyRef.current = hashKey;
      setActiveKey(hashKey);
      return undefined;
    }

    if (hasPendingScrollRestore) return undefined;

    const frame = window.requestAnimationFrame(syncFromScroll);
    return () => window.cancelAnimationFrame(frame);
  }, [
    groupsKey,
    groups,
    resolveActiveKeyFromScroll,
    getHashKey,
    hasPendingScrollRestore,
  ]);

  useEffect(() => {
    const root = scrollRootRef?.current;
    if (!root || !groups.length) return undefined;

    const hashKey = getHashKey();
    const skipInitialSpy = hashKey !== null || hasPendingScrollRestore;

    const onScroll = () => {
      if (scrollingToRef.current) return;
      if (scrollRafRef.current !== null) {
        cancelAnimationFrame(scrollRafRef.current);
      }
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = null;
        const resolved = resolveActiveKeyFromScroll();
        if (resolved) applyActiveKey(resolved, { syncHash: true });
      });
    };

    if (!skipInitialSpy) {
      onScroll();
    }
    root.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      root.removeEventListener("scroll", onScroll);
      if (scrollRafRef.current !== null) {
        cancelAnimationFrame(scrollRafRef.current);
      }
    };
  }, [
    groupsKey,
    scrollRootRef,
    resolveActiveKeyFromScroll,
    applyActiveKey,
    getHashKey,
    hasPendingScrollRestore,
    groups.length,
  ]);

  useEffect(() => {
    if (!groups.length || hasPendingScrollRestore) return;
    const root = scrollRootRef?.current;
    if (root && root.scrollTop > 0) return;
    const key = getHashKey();
    if (!key) return;
    window.requestAnimationFrame(() =>
      scrollToKey(key, { updateHash: false, behavior: "auto" })
    );
  }, [
    groupsKey,
    scrollToKey,
    groups,
    getHashKey,
    hasPendingScrollRestore,
    scrollRootRef,
  ]);

  useEffect(() => {
    if (!activeKey) return;
    const container = navScrollRef.current;
    const btn = tabBtnRefs.current[activeKey];
    if (!container || !btn) return;

    const cRect = container.getBoundingClientRect();
    const bRect = btn.getBoundingClientRect();
    const margin = 32;

    if (bRect.left < cRect.left + margin) {
      container.scrollTo({
        left: container.scrollLeft + (bRect.left - cRect.left) - margin,
        behavior: "smooth",
      });
    } else if (bRect.right > cRect.right - margin) {
      container.scrollTo({
        left: container.scrollLeft + (bRect.right - cRect.right) + margin,
        behavior: "smooth",
      });
    }
  }, [activeKey]);

  if (!groups.length) {
    return <p className="text-sm text-gray-600">{emptyMessage}</p>;
  }

  return (
    <div>
      <div
        ref={stickyNavRef}
        className="sticky top-0 z-20 -mx-4 mb-6 rounded-t-lg border-b border-gray-200 bg-white px-3 pb-2 pt-4"
      >
        <div
          ref={navScrollRef}
          className="library-nav-scroll max-w-full overflow-x-auto pb-2"
        >
          <nav
            className="inline-flex min-w-max items-center gap-0.5 rounded-full border border-gray-200 bg-gray-50 p-1"
            aria-label={navAriaLabel}
          >
            {groups.map(([key, tips], index) => {
              const isActive = activeKey === key;
              const prevActive =
                index > 0 && activeKey === groups[index - 1][0];
              const showDivider = index > 0 && !isActive && !prevActive;
              const desc = getDescription ? getDescription(key) : "";
              const count = tips.length;
              return (
                <React.Fragment key={key}>
                  {showDivider && (
                    <span
                      aria-hidden
                      className="h-4 w-px flex-shrink-0 self-center bg-gray-300"
                    />
                  )}
                  {!showDivider && index > 0 && (
                    <span
                      aria-hidden
                      className="h-4 w-px flex-shrink-0 self-center bg-transparent"
                    />
                  )}
                  <button
                    ref={el => {
                      tabBtnRefs.current[key] = el;
                    }}
                    type="button"
                    title={desc || undefined}
                    onClick={() => scrollToKey(key)}
                    className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm whitespace-nowrap transition-all ${
                      isActive
                        ? "border-[#974ae4] bg-white font-semibold text-[#7a36c4] shadow-sm"
                        : "border-transparent bg-transparent font-medium text-gray-600 hover:bg-white hover:text-gray-800"
                    }`}
                    aria-current={isActive ? "true" : undefined}
                    aria-label={`${getLabel(key)}, ${count} ${count === 1 ? "item" : "items"}`}
                  >
                    <span aria-hidden="true">{getLabel(key)}</span>
                    <span
                      aria-hidden="true"
                      className={`rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums ${
                        isActive
                          ? "bg-[#974ae4] text-white"
                          : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                </React.Fragment>
              );
            })}
          </nav>
        </div>
      </div>

      {intro ? (
        <LibrarySubtabIntro title={introTitle}>{intro}</LibrarySubtabIntro>
      ) : null}

      <div className="space-y-8">
        {groups.map(([key, tips]) => {
          const desc = getDescription ? getDescription(key) : "";
          return (
            <section
              key={key}
              id={anchorId(key)}
              data-group-key={key}
              ref={el => {
                sectionRefs.current[key] = el;
              }}
              className="overflow-hidden rounded-xl border border-gray-200 shadow-sm"
            >
              <header className="border-b border-l-4 border-b-gray-200 border-l-sky-500 bg-gradient-to-r from-sky-50 to-white px-4 py-3.5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className={`${LIBRARY_TYPE.sectionTitle} m-0`}>
                    {getLabel(key)}
                  </h3>
                  <span className="inline-flex shrink-0 rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-sky-800">
                    {tips.length} {tips.length === 1 ? "item" : "items"}
                  </span>
                </div>
                {desc ? (
                  <p className={`${LIBRARY_TYPE.sectionDesc} mb-0`}>{desc}</p>
                ) : null}
              </header>
              <div className="bg-gray-50/80 p-4">
                {renderTipsGrid ? (
                  renderTipsGrid(key, tips)
                ) : (
                  <div className={browseTipsGridClass(tips.length)}>
                    {tips.map(t => (
                      <TipCard
                        key={t.id}
                        tip={t}
                        suppressCategoryBadge={suppressCategoryBadge}
                      />
                    ))}
                  </div>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default GroupedBrowseLayout;
